import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useGetStoreNoCacheQuery, useUpdateStoreMutation } from '@services';
import { AvaStore, JimboResponse } from '@models';
import { Grid, Box, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { LoadingSpinner } from '@components';
import { useAppDispatch } from '@hooks/redux-custom';
import { LoadingButton } from '@mui/lab';
import { showToast } from '@store/slices/notification';
import { avaStoreSchema } from '../helpers/avaStoreSchema';

type Props = {
  searchDepot: string;
};

export const AvaDepotView: FC<Props> = ({ searchDepot }) => {
  const { data: store, isLoading } = useGetStoreNoCacheQuery({ store: searchDepot });
  const [localStore, setLocalStore] = useState<AvaStore>();
  const [updateStore] = useUpdateStoreMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (store?.[0]) {
      setLocalStore(store[0]);
    }
  }, [store]);

  const handleHoursChange = (fieldName: string, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    setLocalStore((prevState) => {
      if (!prevState || !prevState.opening_hours) return prevState;

      const newHours = [...prevState.opening_hours];
      const updatedHours = {
        ...newHours[index],
        [fieldName]: event.target.value,
      };
      const updatedOpeningHours = [...newHours.slice(0, index), updatedHours, ...newHours.slice(index + 1)];
      return {
        ...prevState,
        opening_hours: updatedOpeningHours,
      };
    });
  };

  const handleFieldChange = (fieldName: string, value: string | boolean | number) => {
    setLocalStore((prevState) => {
      if (!prevState) return prevState;

      const newState = JSON.parse(JSON.stringify(prevState));
      const fieldNames = fieldName.split('.');
      const lastFieldName = fieldNames.pop();
      const nestedProp = fieldNames.reduce((obj, key) => {
        obj[key] = obj[key] || {};
        return obj[key];
      }, newState);

      if (lastFieldName && nestedProp[lastFieldName] !== undefined) {
        switch (typeof nestedProp[lastFieldName]) {
          case 'number':
            nestedProp[lastFieldName] = Number(value);
            break;
          case 'boolean':
            nestedProp[lastFieldName] = value === 'true';
            break;
          default:
            nestedProp[lastFieldName] = value;
        }
      }

      return newState;
    });
  };

  const onSubmit = async () => {
    if (localStore) {
      avaStoreSchema
        .validate(localStore)
        .then(() => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { _id, ...updatedStore } = localStore;

          updateStore(updatedStore)
            .unwrap()
            .then(() => {
              dispatch(
                showToast({
                  message: `Updated - ${localStore.name}`,
                  type: 'success',
                }),
              );
            })
            .catch((error: { data: JimboResponse }) => {
              console.log(error);

              dispatch(
                showToast({
                  message: 'Failed to update store!',
                  type: 'error',
                }),
              );
            });
        })
        .catch((error) => {
          dispatch(
            showToast({
              message: error.message,
              type: 'error',
            }),
          );
        });
    }
  };

  return isLoading || !store ? (
    <LoadingSpinner isOpen={isLoading} />
  ) : (
    <Grid border="1px solid" data-testid="depot-view-grid" alignItems="center" p={2}>
      <Grid container item xs={12} spacing={2} p={2}>
        <Grid container item xs={6} spacing={2}>
          <TextField
            fullWidth
            label="Store Code"
            name="store_code"
            margin="normal"
            value={localStore?.store_code || ''}
            InputLabelProps={{ shrink: true }}
            disabled
          />
          <TextField
            fullWidth
            label="Name"
            name="name"
            margin="normal"
            value={localStore?.name || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('name', e.target.value)}
          />
          <TextField
            fullWidth
            label="Type"
            name="store_type"
            margin="normal"
            value={localStore?.store_type || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('store_type', e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="is-trading-label">Is Trading</InputLabel>
            <Select
              labelId="is-trading-label"
              id="is-trading-select"
              name="is_trading"
              value={localStore?.is_trading ? 'true' : 'false'}
              onChange={(e) => handleFieldChange('is_trading', e.target.value)}
              label="Is Trading"
            >
              <MenuItem value={'true'}>True</MenuItem>
              <MenuItem value={'false'}>False</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Address Line 1"
            name="address_line_1"
            margin="normal"
            value={localStore?.address.address_line_1 || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('address.address_line_1', e.target.value)}
          />
          <TextField
            fullWidth
            label="Suburb"
            name="suburb"
            margin="normal"
            value={localStore?.address.suburb || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('address.suburb', e.target.value)}
          />
          <TextField
            fullWidth
            label="Post code"
            name="postcode"
            margin="normal"
            value={localStore?.address.postcode || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('address.postcode', e.target.value)}
          />
          <TextField
            fullWidth
            label="State"
            name="state"
            margin="normal"
            value={localStore?.address.state || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('address.state', e.target.value)}
          />
          <TextField
            fullWidth
            label="Latitude"
            name="lat"
            margin="normal"
            value={localStore?.address.geo.lat || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('address.geo.lat', e.target.value)}
          />
          <TextField
            fullWidth
            label="Longitude"
            name="lng"
            margin="normal"
            value={localStore?.address.geo.lng || ''}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleFieldChange('address.geo.lng', e.target.value)}
          />
        </Grid>
        <Grid container item xs={6} spacing={2}>
          {localStore?.opening_hours.map((item, index) => (
            <Grid container item xs={12} key={`main-${index}`}>
              <Grid container item xs={4} key={`day-${index}`}>
                <TextField key={`text-day-${index}`} fullWidth margin="normal" value={item.day || ''} disabled />
              </Grid>
              <Grid container item xs={4} key={`open-${index}`}>
                <TextField
                  key={`text-open-${index}`}
                  fullWidth
                  label="Open"
                  name={`open`}
                  margin="normal"
                  value={item.open || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => handleHoursChange('open', event, index)}
                />
              </Grid>
              <Grid container item xs={4} key={`close-${index}`}>
                <TextField
                  key={`text-closed-${index}`}
                  fullWidth
                  label="Close"
                  name={`closed`}
                  margin="normal"
                  value={item.closed || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={(event) => handleHoursChange('closed', event, index)}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Box marginTop={2} display="flex" justifyContent="flex-end">
        <LoadingButton loading={isLoading} variant="contained" type="submit" onClick={onSubmit}>
          Save
        </LoadingButton>
      </Box>
    </Grid>
  );
};
